/**
 * Caller-supplied request headers for the authenticated fetch wrappers.
 *
 * `headers` is deliberately not a RequestInit field of `AuthenticatedFetchOptions`:
 * the wrappers own the auth, organization and CSRF headers, and a
 * caller-supplied `headers` object used to replace them wholesale — silently,
 * because the public type omitted the field while the runtime spread it.
 * `extraHeaders` is the one door, and it is merged UNDER the wrapper's own
 * headers: a caller can add `Idempotency-Key` (#765), never override
 * `Authorization` or `X-Selected-Org-ID`.
 *
 * A module of its own so both wrappers (`lib/fetch-utils`, the embedded hook)
 * share one rule without the hook importing the whole fetch layer — tests
 * that stub `fetchJSON` keep working.
 */

/**
 * An answer of the server the CALLER expects and handles (#1751): a refusal the
 * screen explains to the user, not a failure. `fetchWithRetry` logs it as a
 * warning instead of an error event; the thrown `ApiError` is the same.
 * `code` — the `code` field of the JSON body; `error` — its `error` field, where
 * the server's `ApiError` puts its code. Each one named must match; with
 * neither, any body with the status matches.
 */
export interface ExpectedRefusal {
  status: number;
  code?: string;
  error?: string;
}

export type AuthenticatedFetchOptions = Omit<RequestInit, 'headers'> & {
  extraHeaders?: Record<string, string>;
  /**
   * The request only READS access-scoped data though its method is not GET
   * (a search whose filters do not fit a query string): its late answer of an
   * older access revision is refused like a GET's. A write — anything the
   * server commits — must never declare it (E1.5 PR 4b, Codex P1 on #1745).
   * Consumed by the wrappers; never sent.
   */
  accessRead?: boolean;
  /**
   * Answers this request is expected to get besides success — the caller
   * declares them, the shared layer carries no list of routes (#1751). Both
   * wrappers (portal and embedded) consume it; never sent.
   */
  expectedRefusals?: readonly ExpectedRefusal[];
};

/**
 * Caller extras under the wrapper's own headers. A plain record, not a
 * `Headers` object, so the wire shape (and the key casing the CSRF refresh
 * path reads) stays what it was. A reserved name in any casing
 * (`authorization`, `x-selected-org-id`) is dropped from the extras — the
 * wrapper's value is the only one that ships.
 */
export function layerHeaders(
  extra: Record<string, string> | undefined,
  own: Record<string, string>
): Record<string, string> {
  if (!extra) return own;
  const reserved = new Set(Object.keys(own).map((key) => key.toLowerCase()));
  const layered: Record<string, string> = {};
  for (const [key, value] of Object.entries(extra)) {
    if (!reserved.has(key.toLowerCase())) layered[key] = value;
  }
  return { ...layered, ...own };
}
